/**
 * Voice Memo Drop Zone Component
 * Drag-and-drop zone for uploading voice memo files
 */

import { useState, useRef, useCallback } from 'react';
import { Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface VoiceMemoDropZoneProps {
  onFilesDropped: (files: File[]) => void;
  isUploading?: boolean;
}

export function VoiceMemoDropZone({ onFilesDropped, isUploading }: VoiceMemoDropZoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files).filter(file =>
      file.type.startsWith('audio/') ||
      ['.m4a', '.m4b', '.wav', '.mp3'].some(ext => file.name.toLowerCase().endsWith(ext))
    );

    if (files.length > 0) {
      onFilesDropped(files);
    }
  }, [onFilesDropped]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      onFilesDropped(files);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [onFilesDropped]);

  return (
    <div
      className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors mb-6 ${
        isDragging
          ? 'border-primary bg-primary/10'
          : 'border-border hover:border-primary/50'
      } ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {isUploading ? (
        <>
          <div className="w-12 h-12 mx-auto mb-4 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <h3 className="text-lg font-semibold mb-2">Uploading files...</h3>
        </>
      ) : (
        <>
          <Upload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold mb-2">
            {isDragging ? 'Drop files here' : 'Drag & drop audio files'}
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Supported: .m4a, .m4b, .wav, .mp3 (max 25MB per file)
          </p>
          <input
            ref={fileInputRef}
            type="file"
            id="voice-memo-upload"
            className="hidden"
            multiple
            accept=".m4a,.m4b,.wav,.mp3,audio/*"
            onChange={handleFileInput}
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
          >
            Or click to browse
          </Button>
        </>
      )}
    </div>
  );
}
